import React, { useCallback, useEffect, useState, useRef } from "react";

import AddLocationAltIcon from "@mui/icons-material/AddLocationAlt";
import PlaceIcon from "@mui/icons-material/Place";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import Layout4 from "../../DataLayouts/Layout4";
import { fetchOrganizationLocation } from "../../../Apis/OrganizationLocation";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import toast from "react-hot-toast";
import { MAIN_URL } from "../../../Configurations/Urls";
import TableDataGeneric from "../../../Configurations/TableDataGeneric";
import { useNavigate, useSearchParams } from "react-router-dom";
import { operatorRequiresValue } from "../../../Components/filters/operators";

const DEFAULT_COLUMNS = [
  {
    field: "location_name",
    label: "Location Name",
    visible: true,
    width: 200,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "postal_code",
    label: "Postal Code",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "city_name",
    label: "City",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
];

const DEFAULT_PAGINATION = {
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 1,
  hasNext: false,
  hasPrev: false,
};

const DEFAULT_URL_PARAMS = {
  page: "1",
  pageSize: "10",
  sortBy: "organization_location_id",
  sortOrder: "desc",
};

const API_FIELD_ALIASES = {
  City: "city_name",
};

const resolveApiField = (field) => API_FIELD_ALIASES[field] || field;

const normalizeLocationColumns = (columns) =>
  (columns || []).map((col) =>
    col?.field === "City" ? { ...col, field: "city_name" } : col
  );

// Helper functions
const buildFiltersFromParams = (searchParams) => {
  const filters = [];
  const processedFields = new Set();

  searchParams.forEach((value, key) => {
    if (
      key.endsWith("_operator") ||
      key.endsWith("_logic") ||
      !key.startsWith("filter_")
    )
      return;

    const field = key.replace("filter_", "");
    if (processedFields.has(field)) return;
    processedFields.add(field);

    const operator = searchParams.get(`filter_${field}_operator`) || "contains";
    const requiresValue = operatorRequiresValue(operator);

    if (!requiresValue || value) {
      let filterValue = requiresValue ? value : null;

      if (requiresValue && filterValue) {
        if (["equals", "not_equals"].includes(operator)) {
          filterValue = filterValue
            .split(",")
            .map((v) => v.trim().toLowerCase());
        } else if (["between", "not_between"].includes(operator)) {
          filterValue = filterValue.split(",").map((v) => v.trim());
        }
      }

      filters.push({
        field: resolveApiField(field),
        operator,
        value: filterValue,
      });
    }
  });

  const search = searchParams.get("search");
  if (search) {
    filters.push({
      field: "location_name",
      operator: "contains",
      value: search,
    });
  }

  return filters;
};

function OrganizationLocationList() {
  const { userData } = useAuthStore();
  const org = userData?.organization;
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableConfig, setTableConfig] = useState(null);
  const [configColumns, setConfigColumns] = useState(DEFAULT_COLUMNS);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [paginationData, setPaginationData] = useState(DEFAULT_PAGINATION);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fetchControllerRef = useRef(null);
  const hasInitializedURL = useRef(false);
  const [listRefreshKey, setListRefreshKey] = useState(0);

  // Load table configuration from general-datagrids API
  useEffect(() => {
    const loadTableConfiguration = async () => {
      if (!org?.organization_id) {
        setLoadingConfig(false);
        return;
      }

      try {
        const configRes = await fetch(`${MAIN_URL}/api/general-datagrids`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });

        if (configRes.ok) {
          const configResponse = await configRes.json();
          const datagrids = configResponse.datagrids || [];
          const orgKey = `Locations_grid_${org.organization_id}`;
          const savedConfig = datagrids.find(
            (dg) => dg.datagrid_key === orgKey
          );

          if (savedConfig) {
            const serverCfg = savedConfig.datagrid_default_configuration;
            setTableConfig(serverCfg);

            if (
              serverCfg?.columns &&
              Array.isArray(serverCfg.columns) &&
              serverCfg.columns?.length > 0
            ) {
              setConfigColumns(normalizeLocationColumns(serverCfg.columns));
            } else {
              setConfigColumns(DEFAULT_COLUMNS);
            }
          } else {
            setConfigColumns(DEFAULT_COLUMNS);
          }
        }
      } catch (error) {
        console.error("Error loading table configuration:", error);
        setConfigColumns(DEFAULT_COLUMNS);
      } finally {
        setLoadingConfig(false);
      }
    };

    loadTableConfiguration();
  }, [org?.organization_id]);

  // Initialize URL parameters
  useEffect(() => {
    if (hasInitializedURL.current) return;

    const params = new URLSearchParams(searchParams);
    let needsUpdate = false;

    Object.entries(DEFAULT_URL_PARAMS).forEach(([key, defaultValue]) => {
      if (!params.has(key)) {
        params.set(key, defaultValue);
        needsUpdate = true;
      }
    });

    if (needsUpdate) {
      setSearchParams(params, { replace: true });
    }

    hasInitializedURL.current = true;
  }, [searchParams, setSearchParams]);

  // Load locations data
  useEffect(() => {
    if (!org?.organization_id || !hasInitializedURL.current) return;

    fetchControllerRef.current?.abort();
    fetchControllerRef.current = new AbortController();

    setLoading(true);

    const fetchData = async () => {
      try {
        const filters = buildFiltersFromParams(searchParams);
        const requestBody = {
          filters,
          page: Number.parseInt(searchParams.get("page"), 10) || 1,
          pageSize: Number.parseInt(searchParams.get("pageSize"), 10) || 10,
          sort: {
            field: resolveApiField(
              searchParams.get("sortBy") || "organization_location_id"
            ),
            direction: searchParams.get("sortOrder") || "desc",
          },
        };

        // Try new POST endpoint first, fallback to old GET endpoint if needed
        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org.organization_id}/locations-filtered`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
            signal: fetchControllerRef.current.signal,
          }
        );

        let data;
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            data = result.data;
            
            if (result.pagination) {
              const pageSize =
                result.pagination.pageSize || result.pagination.limit || 10;
              setPaginationData({
                page:
                  result.pagination.page ||
                  Number.parseInt(searchParams.get("page"), 10) ||
                  1,
                pageSize,
                total: result.pagination.total || 0,
                totalPages: result.pagination.totalPages || 1,
                hasNext: result.pagination.hasNext || false,
                hasPrev: result.pagination.hasPrev || false,
              });
            }
          } else {
            data = [];
            setPaginationData(DEFAULT_PAGINATION);
          }
        } else if (response.status === 404) {
          // Fallback to old GET endpoint if POST endpoint doesn't exist yet
          const oldData = await fetchOrganizationLocation(org.organization_id);
          data = oldData?.locations || [];
          setPaginationData(DEFAULT_PAGINATION);
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Transform data - preserve existing transformation logic
        let b = data.map((item) => {
          const locationId =
            item.organization_location_id ?? item.id ?? null;
          return {
            ...item,
            organization_location_id: locationId,
            id: locationId,
            City: item?.city_name,
            Country: item?.country_name,
            State: item?.state_name,
            location_latitude:
              item?.location_latitude == null ? "" : item?.location_latitude,
            location_longitude:
              item?.location_longitude == null
                ? ""
                : item?.location_longitude,
            postal_code: item?.postal_code == null ? "" : item?.postal_code,
            area_sq_ft: item?.area_sq_ft == null ? "" : item?.area_sq_ft,
            number_of_floors:
              item?.number_of_floors == null ? "" : item?.number_of_floors,
            LocationOwnershiptype:
              item?.location_ownership_type_name ??
              "",
          };
        });
        setLocations(b);
      } catch (error) {
        if (error.name === "AbortError") {
          return; // Request was cancelled, ignore
        }
        console.error("Error fetching locations:", error);
        toast.error("Failed to load locations");
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      fetchControllerRef.current?.abort();
    };
  }, [org?.organization_id, searchParams, listRefreshKey]);

  const deletelocation = useCallback(
    async (idOrItem) => {
      const locationId =
        typeof idOrItem === "object" && idOrItem !== null
          ? idOrItem.organization_location_id ?? idOrItem.id
          : idOrItem;
      const parsedLocationId = Number(locationId);

      if (!Number.isInteger(parsedLocationId) || parsedLocationId <= 0) {
        toast.error("Invalid location selected for deletion.");
        return;
      }

      try {
        const org_id = org.organization_id;
        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org_id}/location/${parsedLocationId}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        if (response.ok) {
          toast.success("Location deleted successfully");
          setLocations((prev) =>
            prev.filter(
              (item) =>
                Number(item.organization_location_id ?? item.id) !==
                parsedLocationId
            )
          );
          setPaginationData((prev) => ({
            ...prev,
            total: Math.max(0, (prev.total || 0) - 1),
          }));
          setListRefreshKey((key) => key + 1);
        } else {
          const errorData = await response.json();
          if (response.status === 401) {
            toast.error("Session Expired!");
            window.location.href = "/login";
          } else {
            toast.error(
              errorData?.errors?.organization_location_id?.[0] ||
                errorData?.error ||
                "Failed to delete location"
            );
          }
        }
      } catch (error) {
        console.error("Delete failed:", error);
        toast.error("Failed to delete location");
      }
    },
    [org?.organization_id]
  );

  const handleEdit = useCallback(
    (item) => {
      navigate(`/organization/location/edit/${item.id}`);
    },

    [navigate]
  );



       const handleShow = useCallback(
    (item) => {
      navigate(`/organization/location/view/${item.id}`)
    },
    [navigate],
  )

  const handleSortChange = useCallback(
    (field, direction) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("sortBy", resolveApiField(field));
          next.set("sortOrder", direction);
          next.set("page", "1");
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const handleFilterChange = useCallback(
    (filtersOrUpdates) => {
      setSearchParams(
        (prev) => {
          const prevParams = new URLSearchParams(prev);
          const next = new URLSearchParams(prev);

          const buildFilterSignature = (params) =>
            [...params.keys()]
              .filter((k) => k.startsWith("filter_") || k === "search")
              .sort()
              .map((k) => `${k}=${params.get(k) ?? ""}`)
              .join("&");

          const prevSignature = buildFilterSignature(prevParams);

          if (Array.isArray(filtersOrUpdates)) {
            [...next.keys()]
              .filter((k) => k.startsWith("filter_"))
              .forEach((k) => next.delete(k));
            filtersOrUpdates.forEach((f) => {
              if (f?.field != null) {
                next.set(`filter_${f.field}`, f.value ?? "");
                if (f.operator) {
                  next.set(`filter_${f.field}_operator`, f.operator);
                }
              }
            });
          } else if (
            filtersOrUpdates &&
            typeof filtersOrUpdates === "object" &&
            filtersOrUpdates.searchQuery !== undefined
          ) {
            if (String(filtersOrUpdates.searchQuery).trim() !== "") {
              next.set("search", filtersOrUpdates.searchQuery.trim());
            } else {
              next.delete("search");
            }
          }

          const nextSignature = buildFilterSignature(next);
          if (prevSignature !== nextSignature) {
            next.set("page", "1");
          }

          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  return (
    <>
      <Layout4
        loading={loading}
        heading={" Locations"}
        btnName={"Add Location"}
        add_action={"LOCATION_ADD"}
        Data={locations}
        delete_action={"LOCATION_DELETE"}
        tableHeaders={[
          {
            name: "Location ",
            value_key: "location_name",
            width: "130px",
            textStyle: "capitalize",
          },
          { name: "Address ", value_key: "addressline1", width: "200px" },
          {
            name: "Country",
            value_key: "general_country_id",
            width: "100px",
            textStyle: "capitalize",
          },
          {
            name: "State",
            value_key: "general_state_id",
            width: "130px",
            textStyle: "capitalize",
          },
          {
            name: "City",
            value_key: "general_city_id",
            width: "130px",
            textStyle: "capitalize",
          },
          { name: "Postal Code", value_key: "postal_code", width: "130px" },
        ]}
        Icons={[
          <AddLocationAltIcon
            sx={{ fontSize: 60, color: "grey.500", mb: 2 }}
          />,
          <PlaceIcon color="primary" />,
          <PlaceIcon color="primary" />,

          <LocationCityIcon sx={{ color: "text.secondary" }} />,
        ]}
        messages={[
          "locations",
          "organisation locations",
          "Add Location",
          "Location, Removing this location will delete its associated organizations and all related data permanently.",
        ]}
        Route={"/organization/location"}
        setData={setLocations}
        DeleteFunc={deletelocation}
      />

      {!loadingConfig && (
        <TableDataGeneric
          tableName="organization_locations"
          primaryKey="organization_location_id"
          heading="Locations"
          data={locations}
          sortname="location_name"
          showActions={true}
          Route="/organization/location"
          DeleteFunc={deletelocation}
          EditFunc={handleEdit}
          handleShow={handleShow}
          onSortChange={handleSortChange}
          onFilterChange={handleFilterChange}
          edit_delete_action={["LOCATION_EDIT", "LOCATION_DELETE"]}
          token={localStorage.getItem("token")}
          configss={configColumns}
          paginationData={paginationData}
          filterModule="organization_locations"
          {...(tableConfig && { config: tableConfig })}
        />
      )}
    </>
  );
}

export default OrganizationLocationList;
